import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { useI18n } from "@/i18n/I18nProvider";
import { supabase } from "@/integrations/supabase/client";
import { supabaseUntyped } from "@/integrations/supabase/untyped";
import { Calendar as CalendarIcon, Clock, MapPin, AlertCircle } from "lucide-react";
import type { ExternalBookingRow, ExternalSchedulingMode, ExternalBookingStatus } from "./types";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyId: string;
  /** Optional row to edit; when provided the dialog acts as edit form. */
  initial?: ExternalBookingRow | null;
  /** Optional list of services this company offers (for the dropdown). */
  services?: Array<{ id: string; name: string }>;
  onSaved?: (row: ExternalBookingRow) => void;
}

interface FormState {
  scheduling_mode: ExternalSchedulingMode;
  // Exact mode
  exact_date: string;     // yyyy-mm-dd
  exact_time: string;     // HH:mm
  exact_duration: string; // minutes (free text)
  // Window with times
  win_checkout_at: string; // datetime-local
  win_checkin_at: string;
  // Date-only window
  win_checkout_date: string;
  win_checkin_date: string;
  // Identification
  title: string;
  address: string;
  guest_name: string;
  guest_phone: string;
  guest_email: string;
  service_label: string;
  service_id: string;
  // Operational
  guests: string;
  has_pets: boolean;
  has_kids: boolean;
  amount: string;
  notes: string;
  status: ExternalBookingStatus;
}

const EMPTY: FormState = {
  scheduling_mode: "window",
  exact_date: "",
  exact_time: "",
  exact_duration: "",
  win_checkout_at: "",
  win_checkin_at: "",
  win_checkout_date: "",
  win_checkin_date: "",
  title: "",
  address: "",
  guest_name: "",
  guest_phone: "",
  guest_email: "",
  service_label: "",
  service_id: "",
  guests: "",
  has_pets: false,
  has_kids: false,
  amount: "",
  notes: "",
  status: "planned",
};

function toLocalInput(iso: string | null, mode: "datetime" | "date"): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return mode === "date" ? `${yyyy}-${mm}-${dd}` : `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
}

function fromLocalInput(value: string): Date | null {
  if (!value) return null;
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
}

function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function endOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
}

export default function ExternalBookingForm({ open, onOpenChange, companyId, initial, services, onSaved }: Props) {
  const { toast } = useToast();
  const { t } = useI18n();
  const [form, setForm] = useState<FormState>(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const isEdit = !!initial?.id;

  useEffect(() => {
    if (!open) return;
    if (initial) {
      const startIso = initial.start_at;
      const endIso = initial.end_at;
      setForm({
        scheduling_mode: initial.scheduling_mode,
        exact_date: initial.scheduling_mode === "exact" ? toLocalInput(startIso, "date") : "",
        exact_time:
          initial.scheduling_mode === "exact" && startIso
            ? new Date(startIso).toTimeString().slice(0, 5)
            : "",
        exact_duration: initial.duration_minutes != null ? String(initial.duration_minutes) : "",
        win_checkout_at: initial.scheduling_mode === "window" ? toLocalInput(startIso, "datetime") : "",
        win_checkin_at: initial.scheduling_mode === "window" ? toLocalInput(endIso, "datetime") : "",
        win_checkout_date: initial.scheduling_mode === "date_only" ? toLocalInput(startIso, "date") : "",
        win_checkin_date: initial.scheduling_mode === "date_only" ? toLocalInput(endIso, "date") : "",
        title: initial.title ?? "",
        address: initial.address ?? "",
        guest_name: initial.guest_name ?? "",
        guest_phone: initial.guest_phone ?? "",
        guest_email: initial.guest_email ?? "",
        service_label: initial.service_label ?? "",
        service_id: initial.service_id ?? "",
        guests: initial.guests != null ? String(initial.guests) : "",
        has_pets: !!initial.has_pets,
        has_kids: !!initial.has_kids,
        amount: initial.amount != null ? String(initial.amount) : "",
        notes: initial.notes ?? "",
        status: initial.status,
      });
    } else {
      setForm(EMPTY);
    }
  }, [open, initial]);

  const validation = useMemo(() => validate(form), [form]);

  const handleSubmit = async () => {
    if (!validation.ok) {
      toast({ title: "Verifica i campi", description: validation.errors[0], variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      const { data: sess } = await supabase.auth.getSession();
      const userId = sess?.session?.user?.id;
      if (!userId) {
        toast({ title: t("vendor.tracker.session_expired"), description: t("vendor.tracker.sign_in_again"), variant: "destructive" });
        return;
      }

      const { start_at, end_at, all_day } = computeTimes(form);

      const payload: Record<string, unknown> = {
        company_id: companyId,
        created_by_user_id: userId,
        scheduling_mode: form.scheduling_mode,
        start_at: start_at ? start_at.toISOString() : null,
        end_at: end_at ? end_at.toISOString() : null,
        all_day,
        title: form.title.trim() || null,
        address: form.address.trim() || null,
        guest_name: form.guest_name.trim() || null,
        guest_phone: form.guest_phone.trim() || null,
        guest_email: form.guest_email.trim() || null,
        service_label: form.service_label.trim() || null,
        service_id: form.service_id || null,
        duration_minutes: form.exact_duration ? Number(form.exact_duration) : null,
        guests: form.guests ? Number(form.guests) : null,
        has_pets: form.has_pets,
        has_kids: form.has_kids,
        amount: form.amount ? Number(form.amount) : null,
        currency: "EUR",
        status: form.status,
        notes: form.notes.trim() || null,
      };

      let saved: ExternalBookingRow | null = null;
      if (isEdit && initial) {
        const { data, error } = await supabaseUntyped
          .from("external_bookings")
          .update(payload)
          .eq("id", initial.id)
          .select("*")
          .maybeSingle();
        if (error) throw error;
        saved = data as ExternalBookingRow;
      } else {
        const { data, error } = await supabaseUntyped
          .from("external_bookings")
          .insert(payload)
          .select("*")
          .maybeSingle();
        if (error) throw error;
        saved = data as ExternalBookingRow;
      }

      toast({
        title: isEdit ? "Booking aggiornato" : "Booking esterno aggiunto",
        description: t("vendor.tracker.saved_desc"),
      });
      onSaved?.(saved!);
      onOpenChange(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Impossibile salvare il booking esterno.";
      toast({ title: t("vendor.tracker.error"), description: message, variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => setForm((prev) => ({ ...prev, [key]: value }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Modifica booking esterno" : "Nuovo booking esterno"}</DialogTitle>
          <DialogDescription>
            Inserisci una prenotazione gestita fuori dalla piattaforma. Servirà solo per il tuo
            tracking, non finirà nelle prenotazioni Halomora e non genera commissioni.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {/* Scheduling mode */}
          <div className="space-y-2">
            <Label>Tipo di programmazione</Label>
            <Tabs
              value={form.scheduling_mode}
              onValueChange={(v) => setField("scheduling_mode", v as ExternalSchedulingMode)}
            >
              <TabsList className="grid grid-cols-2 sm:grid-cols-4 w-full bg-slate-100/70">
                <TabsTrigger value="exact" className="text-xs sm:text-sm">Orario esatto</TabsTrigger>
                <TabsTrigger value="window" className="text-xs sm:text-sm">Checkout/Check-in</TabsTrigger>
                <TabsTrigger value="date_only" className="text-xs sm:text-sm">Solo giorno</TabsTrigger>
                <TabsTrigger value="flexible" className="text-xs sm:text-sm">Flessibile</TabsTrigger>
              </TabsList>

              <TabsContent value="exact" className="mt-3">
                <p className="text-xs text-slate-600 mb-3">Il cliente ha indicato data e orario precisi della pulizia.</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs">Data</Label>
                    <Input type="date" value={form.exact_date} onChange={(e) => setField("exact_date", e.target.value)} />
                  </div>
                  <div>
                    <Label className="text-xs">Ora</Label>
                    <Input type="time" value={form.exact_time} onChange={(e) => setField("exact_time", e.target.value)} />
                  </div>
                  <div>
                    <Label className="text-xs">Durata stimata (min)</Label>
                    <Input
                      type="number"
                      placeholder="120"
                      value={form.exact_duration}
                      onChange={(e) => setField("exact_duration", e.target.value)}
                    />
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="window" className="mt-3">
                <p className="text-xs text-slate-600 mb-3">
                  Hai gli orari precisi: <strong>checkout</strong> degli ospiti attuali e <strong>check-in</strong> dei prossimi.
                  La pulizia va programmata in mezzo.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Checkout (uscita ospiti attuali)</Label>
                    <Input
                      type="datetime-local"
                      value={form.win_checkout_at}
                      onChange={(e) => setField("win_checkout_at", e.target.value)}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Check-in (arrivo prossimi ospiti)</Label>
                    <Input
                      type="datetime-local"
                      value={form.win_checkin_at}
                      onChange={(e) => setField("win_checkin_at", e.target.value)}
                    />
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="date_only" className="mt-3">
                <p className="text-xs text-slate-600 mb-3">
                  Solo i giorni: <strong>uscita ospiti attuali</strong> e <strong>arrivo prossimi ospiti</strong> (orari non comunicati).
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Giorno checkout (uscita ospiti attuali)</Label>
                    <Input
                      type="date"
                      value={form.win_checkout_date}
                      onChange={(e) => setField("win_checkout_date", e.target.value)}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Giorno check-in (arrivo prossimi ospiti)</Label>
                    <Input
                      type="date"
                      value={form.win_checkin_date}
                      onChange={(e) => setField("win_checkin_date", e.target.value)}
                    />
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="flexible" className="mt-3">
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 flex gap-2">
                  <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <div>
                    Il cliente ha chiesto a te quando organizzare la pulizia. Questa entry rimarrà
                    senza data fissa ma comparirà negli elenchi "Da pianificare".
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </div>

          {/* Identification */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs flex items-center gap-1"><MapPin className="h-3 w-3" /> Etichetta / appartamento</Label>
              <Input
                placeholder="Apt. Piazza Navona"
                value={form.title}
                onChange={(e) => setField("title", e.target.value)}
              />
            </div>
            <div>
              <Label className="text-xs">Indirizzo</Label>
              <Input
                placeholder="Via Roma 12, Milano"
                value={form.address}
                onChange={(e) => setField("address", e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Cliente / referente</Label>
              <Input value={form.guest_name} onChange={(e) => setField("guest_name", e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">Telefono</Label>
              <Input value={form.guest_phone} onChange={(e) => setField("guest_phone", e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">Email</Label>
              <Input value={form.guest_email} onChange={(e) => setField("guest_email", e.target.value)} />
            </div>
          </div>

          {/* Service */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Servizio (descrizione libera)</Label>
              <Input
                placeholder="Es. Pulizia fondo + cambio biancheria"
                value={form.service_label}
                onChange={(e) => setField("service_label", e.target.value)}
              />
            </div>
            <div>
              <Label className="text-xs">Collega a uno dei tuoi servizi (opzionale)</Label>
              <Select value={form.service_id || "__none__"} onValueChange={(v) => setField("service_id", v === "__none__" ? "" : v)}>
                <SelectTrigger>
                  <SelectValue placeholder={t("vendor.tracker.none")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">{t("vendor.tracker.none")}</SelectItem>
                  {(services || []).map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs">Ospiti</Label>
              <Input type="number" value={form.guests} onChange={(e) => setField("guests", e.target.value)} />
            </div>
            <div className="flex items-center gap-2 pt-5">
              <Checkbox checked={form.has_pets} onCheckedChange={(v) => setField("has_pets", Boolean(v))} />
              <Label className="text-xs">Animali</Label>
            </div>
            <div className="flex items-center gap-2 pt-5">
              <Checkbox checked={form.has_kids} onCheckedChange={(v) => setField("has_kids", Boolean(v))} />
              <Label className="text-xs">Bambini</Label>
            </div>
            <div>
              <Label className="text-xs">Importo (EUR, opz.)</Label>
              <Input type="number" placeholder="0,00" value={form.amount} onChange={(e) => setField("amount", e.target.value)} />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Stato</Label>
              <Select value={form.status} onValueChange={(v) => setField("status", v as ExternalBookingStatus)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="planned">Pianificato</SelectItem>
                  <SelectItem value="in_progress">In corso</SelectItem>
                  <SelectItem value="completed">Completato</SelectItem>
                  <SelectItem value="cancelled">Annullato</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-xs">Note</Label>
            <Textarea rows={3} value={form.notes} onChange={(e) => setField("notes", e.target.value)} />
          </div>

          {!validation.ok && (
            <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-xs text-red-700 flex gap-2">
              <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <ul className="space-y-0.5">
                {validation.errors.map((e, i) => (
                  <li key={i}>{e}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Annulla
          </Button>
          <Button onClick={handleSubmit} disabled={submitting || !validation.ok}>
            {submitting ? t('vendor.tracker.saving') : isEdit ? t('vendor.tracker.update') : t('vendor.tracker.add')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ----- helpers -----

function validate(form: FormState): { ok: boolean; errors: string[] } {
  const errors: string[] = [];
  if (!form.title.trim() && !form.address.trim() && !form.guest_name.trim()) {
    errors.push("Inserisci almeno l'etichetta, l'indirizzo o il nome del cliente.");
  }
  switch (form.scheduling_mode) {
    case "exact":
      if (!form.exact_date) errors.push("Seleziona la data del servizio.");
      if (!form.exact_time) errors.push("Seleziona l'orario del servizio.");
      break;
    case "window": {
      const a = fromLocalInput(form.win_checkout_at);
      const b = fromLocalInput(form.win_checkin_at);
      if (!a || !b) {
        errors.push("Inserisci checkout e check-in con orario.");
      } else if (b.getTime() <= a.getTime()) {
        errors.push("Il check-in deve essere successivo al checkout.");
      }
      break;
    }
    case "date_only": {
      const a = form.win_checkout_date ? new Date(form.win_checkout_date) : null;
      const b = form.win_checkin_date ? new Date(form.win_checkin_date) : null;
      if (!a || !b) {
        errors.push("Inserisci entrambi i giorni (checkout e check-in).");
      } else if (b.getTime() < a.getTime()) {
        errors.push("Il giorno di check-in deve essere uguale o successivo al checkout.");
      }
      break;
    }
    case "flexible":
      // No date validation — just optionally requires identification
      break;
  }
  return { ok: errors.length === 0, errors };
}

function computeTimes(form: FormState): { start_at: Date | null; end_at: Date | null; all_day: boolean } {
  switch (form.scheduling_mode) {
    case "exact": {
      if (!form.exact_date || !form.exact_time) return { start_at: null, end_at: null, all_day: false };
      const start = new Date(`${form.exact_date}T${form.exact_time}`);
      let end: Date | null = null;
      if (form.exact_duration) {
        end = new Date(start.getTime() + Number(form.exact_duration) * 60_000);
      }
      return { start_at: start, end_at: end, all_day: false };
    }
    case "window": {
      const a = fromLocalInput(form.win_checkout_at);
      const b = fromLocalInput(form.win_checkin_at);
      return { start_at: a, end_at: b, all_day: false };
    }
    case "date_only": {
      const a = form.win_checkout_date ? startOfDay(new Date(form.win_checkout_date)) : null;
      const b = form.win_checkin_date ? endOfDay(new Date(form.win_checkin_date)) : null;
      return { start_at: a, end_at: b, all_day: true };
    }
    case "flexible":
      return { start_at: null, end_at: null, all_day: false };
  }
}
